// frontend/src/pages/Signup.js
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { signupRequest } from '../api/api';
import RequestSubmitted from './RequestSubmitted';

const HOUSES = ["Savant", "Junbi", "Tafakari", "Umusa", "Laulima"];

export default function Signup() {
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName]   = useState('');

  const [role, setRole] = useState('scholar');     // 'educator' | 'scholar'
  const [grade, setGrade] = useState('');          // Scholars only
  const [house, setHouse] = useState('');          // Both roles
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const strength = useMemo(() => {
    let s = 0;
    if (pw.length >= 8) s++;
    if (/[A-Z]/.test(pw)) s++;
    if (/[a-z]/.test(pw)) s++;
    if (/\d/.test(pw)) s++;
    if (/[^A-Za-z0-9]/.test(pw)) s++;
    return s; // 0..5
  }, [pw]);

  function validate() {
    if (!agree) return 'You must agree before continuing.';
    if (!firstName || !lastName) return 'Please provide your first and last name.';
    if (pw !== pw2) return 'Passwords do not match.';
    if (strength < 3) return 'Password must be stronger.';
    if (!house) return 'Please select your House.';
    if (role === 'scholar' && !grade) return 'Please select your Grade.';
    return '';
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (busy) return;

    const v = validate();
    if (v) { setError(v); return; }

    setBusy(true);
    setError('');

    try {
      const result = await signupRequest({
        email,
        password: pw,
        desired_role: role,
        first_name: firstName,
        last_name: lastName,
        grade: role === 'scholar' ? grade : undefined,
        house
      });

      if (result.ok) {
        setDone(true);
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('Unexpected error. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return <RequestSubmitted role={role} />;
  }

  return (
    <div style={styles.page}>
      {/* LEFT */}
      <section style={styles.left}>
        <div style={styles.leftCard}>
          <img
            src="/icons/Propel_schools_logo.png"
            alt="Propel Schools"
            style={styles.logo}
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
          <h1 style={styles.h1}>Create your account</h1>
          <p style={styles.lede}>
            Educators and Scholars: provide your name and house. Scholars also provide grade.
          </p>
        </div>
      </section>

      {/* RIGHT */}
      <section style={styles.right}>
        <div style={styles.panel}>
          <h2 style={styles.title}>Create Account</h2>

          {/* Role selector */}
          <div style={styles.roleRow} aria-label="Select your role">
            <button
              type="button"
              aria-pressed={role === 'scholar'}
              onClick={() => setRole('scholar')}
              style={{ ...styles.roleBtn, ...(role === 'scholar' ? styles.roleBtnActive : {}) }}
            >
              Scholar
            </button>
            <button
              type="button"
              aria-pressed={role === 'educator'}
              onClick={() => setRole('educator')}
              style={{ ...styles.roleBtn, ...(role === 'educator' ? styles.roleBtnActive : {}) }}
            >
              Educator
            </button>
          </div>

          <form onSubmit={onSubmit} style={styles.form}>
            {/* Name */}
            <label style={styles.label} htmlFor="first">First name</label>
            <input
              id="first"
              type="text"
              placeholder="Ada"
              value={firstName}
              onChange={(e)=>setFirstName(e.target.value)}
              style={styles.input}
              required
              autoComplete="given-name"
            />

            <label style={styles.label} htmlFor="last">Last name</label>
            <input
              id="last"
              type="text"
              placeholder="Lovelace"
              value={lastName}
              onChange={(e)=>setLastName(e.target.value)}
              style={styles.input}
              required
              autoComplete="family-name"
            />

            <label style={styles.label} htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e)=>setEmail(e.target.value)}
              style={styles.input}
              required
              autoComplete="email"
            />

            <label style={styles.label} htmlFor="pw">Password</label>
            <input
              id="pw"
              type="password"
              placeholder="At least 8 characters"
              value={pw}
              onChange={(e)=>setPw(e.target.value)}
              style={styles.input}
              required
              autoComplete="new-password"
            />
            <PasswordStrength strength={strength} />

            <label style={styles.label} htmlFor="pw2">Confirm Password</label>
            <input
              id="pw2"
              type="password"
              placeholder="Re-enter password"
              value={pw2}
              onChange={(e)=>setPw2(e.target.value)}
              style={styles.input}
              required
              autoComplete="new-password"
            />

            {role === 'scholar' && (
              <>
                <label style={styles.label} htmlFor="grade">Grade</label>
                <select
                  id="grade"
                  value={grade}
                  onChange={(e)=>setGrade(e.target.value)}
                  style={{ ...styles.input, height: 46 }}
                  required
                >
                  <option value="">Select grade</option>
                  <option value="K">K</option>
                  {Array.from({ length: 12 }).map((_, i) => (
                    <option key={i+1} value={String(i+1)}>{i+1}</option>
                  ))}
                </select>
              </>
            )}

            {/* House */}
            <fieldset style={styles.fieldset}>
              <legend style={styles.label}>House</legend>
              <div style={styles.radioGrid}>
                {HOUSES.map(h => (
                  <label key={h} style={styles.radioLabel}>
                    <input
                      type="radio"
                      name="house"
                      value={h}
                      checked={house === h}
                      onChange={() => setHouse(h)}
                      required
                    />
                    <span>{h}</span>
                  </label>
                ))}
              </div>
              <div style={styles.helpText}>Select your House.</div>
            </fieldset>

            <label style={{ ...styles.label, display:'flex', gap:8, alignItems:'center', fontWeight:600 }}>
              <input
                type="checkbox"
                checked={agree}
                onChange={(e)=>setAgree(e.target.checked)}
                aria-label="Agree to terms"
              />
              I understand my account will be reviewed before activation.
            </label>

            <button type="submit" style={styles.primaryBtn} disabled={busy}>
              {busy ? 'Submitting…' : 'Submit Request'}
            </button>

            {error && <div style={styles.error}>{error}</div>}
          </form>

          <div style={styles.footerRow}>
            Already have an account?&nbsp;
            <Link to="/login" style={styles.smallLink}>Sign in</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function PasswordStrength({ strength }) {
  const filled = Math.min(4, Math.max(0, strength - 1)); // 0..4 bars
  return (
    <div style={{ marginTop: 6, marginBottom: 6 }}>
      <div style={{ display:'flex', gap:6 }}>
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            style={{
              height: 8,
              flex: 1,
              borderRadius: 4,
              background: i < filled ? '#ffa71f' : '#89c3e8'
            }}
          />
        ))}
      </div>
      <div style={{ fontSize: 12, color:'#042d60', marginTop: 4 }}>
        Use 8+ chars with letters, numbers, and symbols for a stronger password.
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    display: 'grid',
    gridTemplateColumns: 'minmax(320px, 1fr) 560px',
    background: '#F7F9FC'
  },
  left: {
    background: 'linear-gradient(135deg, #5789be 0%, #89c3e8 100%)',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '64px'
  },
  leftCard: { maxWidth: 600, textAlign: 'left' },
  logo: { width: 260, height: 'auto', marginBottom: 32, filter:'drop-shadow(0 6px 18px rgba(0,0,0,0.18))' },
  h1: { fontSize: 44, lineHeight: 1.15, margin: '8px 0 12px', fontWeight: 800 },
  lede: { fontSize: 18, opacity: 0.98, maxWidth: 520 },

  right: {
    background: '#FFFFFF',
    borderLeft: '1px solid rgba(0,0,0,0.06)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px'
  },
  panel: { width: '100%', maxWidth: 460 },
  title: { fontSize: 32, fontWeight: 800, color:'#003e73', marginBottom: 16 },

  roleRow: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom: 12 },
  roleBtn: {
    height: 40, borderRadius: 999, border: '2px solid #89c3e8',
    background: 'white', color: '#042d60', fontWeight: 800, cursor: 'pointer'
  },
  roleBtnActive: { borderColor: '#003e73', background: '#89c3e8', color: '#003e73' },

  form: { display: 'grid', gap: 8 },
  label: { fontWeight: 700, color: '#042d60', marginTop: 6 },
  input: {
    width: '100%', height: 46, borderRadius: 14, padding: '0 14px',
    border: '1px solid rgba(0,0,0,0.12)', outline: 'none', marginTop: 2
  },
  helpText: { fontSize: 12, color:'#042d60', marginTop: 4 },

  fieldset: {
    border: '1px solid rgba(0,0,0,0.08)',
    borderRadius: 12,
    padding: 12,
    marginTop: 6
  },
  radioGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
    gap: 8,
    marginTop: 6
  },
  radioLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 10px',
    borderRadius: 10,
    background: '#f7f9fc'
  },

  primaryBtn: {
    width: '100%', height: 48, borderRadius: 999, border: 'none',
    fontWeight: 900, cursor: 'pointer', marginTop: 12,
    background: '#f9a21a', color: 'white'
  },

  error: {
    marginTop: 8, color: '#b00020', fontSize: 14,
    background: '#FFEAEA', border: '1px solid #F5B5B5',
    padding: '8px 10px', borderRadius: 8
  },

  footerRow: { marginTop: 12, fontSize: 14, color: '#042d60' },
  smallLink: { color: '#003e73', textDecoration: 'underline' },
};
